import { useEffect } from "react";

export interface HeaderEntryType {
  id: string;
  key: string;
  value: string;
}

let headerIdCounter = 0;

export function createHeaderEntry(key = "", value = ""): HeaderEntryType {
  return { id: `header-${++headerIdCounter}`, key, value };
}

export const MCP_SERVERS_PATH = "/settings/integrations/mcp-servers";

export function buildMcpCredentials(
  authType: string,
  apiKey: string,
  headers: HeaderEntryType[],
  oauthClientId?: string,
): string | undefined {
  if (authType === "API_KEY" && apiKey) {
    return JSON.stringify({ api_key: apiKey });
  }
  if (authType === "CUSTOM_HEADERS") {
    const headersObj: Record<string, string> = {};
    for (const h of headers) {
      if (h.key.trim() && h.value.trim()) {
        headersObj[h.key.trim()] = h.value.trim();
      }
    }
    if (Object.keys(headersObj).length > 0) {
      return JSON.stringify({ headers: headersObj });
    }
  }
  // OAuth servers normally self-register via dynamic client registration. Some providers
  // (e.g. Harness) reject that and require a pre-registered Client ID, so allow supplying one.
  if (authType === "OAUTH" && oauthClientId?.trim()) {
    return JSON.stringify({ client_id: oauthClientId.trim() });
  }
  return undefined;
}

type ToastFnType = (opts: { title: string; description: string; variant?: "default" | "success" | "error" }) => void;

export function useMcpOAuthCallback(toast: ToastFnType): void {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mcpSetup = params.get("mcp_setup");
    if (!mcpSetup) return;

    if (mcpSetup === "success") {
      toast({ title: "MCP Server connected", description: "OAuth authorization completed successfully." });
    } else if (mcpSetup === "error") {
      const errorId = params.get("error_id");
      const errorType = params.get("error");
      const description = errorId
        ? `Please try again. If the issue persists, contact support with error ID: ${errorId}`
        : `OAuth setup failed${errorType ? `: ${errorType}` : ""}. Please try again.`;
      toast({ title: "OAuth setup failed", description, variant: "error" });
    }

    const url = new URL(window.location.href);
    url.searchParams.delete("mcp_setup");
    url.searchParams.delete("error");
    url.searchParams.delete("error_id");
    window.history.replaceState({}, "", url.toString());
  }, [toast]);
}

export function redirectToOAuth(oauthUrl: string, toast: ToastFnType): boolean {
  try {
    const parsed = new URL(oauthUrl);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
      throw new Error("Invalid protocol");
    }
    toast({ title: "Redirecting to OAuth provider...", description: "Complete authorization to connect." });
    window.location.href = parsed.href;
    return true;
  } catch {
    toast({ title: "Error", description: "Invalid OAuth URL received from server.", variant: "error" });
    return true;
  }
}
